/**
 * Cadence Drill — free-practice mode.
 *
 * Hear a chord progression and identify the cadence type.
 * Supports full 4-chord context or short 2-chord context.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import {
  buildChord,
  degreeOffset,
  DIATONIC_TRIAD_QUALITIES,
  pickKey,
  smoothVoicing,
  MODE_DISPLAY_NAMES,
  type KeyChoice,
} from "./utils.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type CadenceType = "PAC" | "IAC" | "plagal" | "half" | "deceptive";
export type CadenceContextLength = "short" | "full";
export type CadenceTempo = "slow" | "medium" | "fast";

export interface CadenceExerciseConfig {
  /** Cadence types to draw from, ≥2. */
  cadences: CadenceType[];
  contextLength: CadenceContextLength;
  tempo: CadenceTempo;
  tonic: "fixed" | "random";
  /** MIDI note for the fixed tonic. Only used when tonic === "fixed". */
  fixedTonicMidi: number;
  drillMode: "graded" | "listening";
  fsrsEnabled: boolean;
}

export interface CadenceExerciseRound {
  cadenceType: CadenceType;
  keyName: string;
  tonicMidi: number;
  contextLength: CadenceContextLength;
  /** Roman numeral labels for all played chords (for reveal display). */
  romanLabels: string[];
  audioPlan: AudioPlan;
  teachAudio: AudioPlan;
}

// ─── Pool metadata ────────────────────────────────────────────────────────────

export interface CadenceTypeInfo {
  longName: string;
  /** Short label for answer buttons. */
  shortLabel: string;
  description: string;
}

export const CADENCE_TYPE_POOL: Record<CadenceType, CadenceTypeInfo> = {
  PAC:       { longName: "Perfect Authentic", shortLabel: "PAC",       description: "V–I, strong resolution" },
  IAC:       { longName: "Imperfect Authentic", shortLabel: "IAC",     description: "V–I⁶, weaker resolution" },
  plagal:    { longName: "Plagal",            shortLabel: "Plagal",    description: "IV–I, \"Amen\" cadence" },
  half:      { longName: "Half",              shortLabel: "Half",      description: "Ends on V, open feeling" },
  deceptive: { longName: "Deceptive",         shortLabel: "Deceptive", description: "V–vi, surprise resolution" },
};

export const CADENCE_TYPES_ORDERED: readonly CadenceType[] = [
  "PAC", "IAC", "plagal", "half", "deceptive",
];

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_CADENCE_EXERCISE_CONFIG: CadenceExerciseConfig = {
  cadences: ["PAC", "plagal", "half", "deceptive"],
  contextLength: "full",
  tempo: "medium",
  tonic: "random",
  fixedTonicMidi: 60,
  drillMode: "graded",
  fsrsEnabled: false,
};

// ─── Cadence specs ────────────────────────────────────────────────────────────

interface CadenceSpec {
  fullDegrees: number[];
  shortDegrees: number[];
  fullRoman: string[];
  shortRoman: string[];
  /** IAC: final I chord in first inversion (3rd in bass). */
  finalFirstInversion: boolean;
}

const CADENCE_SPECS: Record<CadenceType, CadenceSpec> = {
  PAC: {
    fullDegrees:  [1, 4, 5, 1],
    shortDegrees: [5, 1],
    fullRoman:    ["I", "IV", "V", "I"],
    shortRoman:   ["V", "I"],
    finalFirstInversion: false,
  },
  IAC: {
    fullDegrees:  [1, 4, 5, 1],
    shortDegrees: [5, 1],
    fullRoman:    ["I", "IV", "V", "I⁶"],
    shortRoman:   ["V", "I⁶"],
    finalFirstInversion: true,
  },
  plagal: {
    fullDegrees:  [1, 5, 4, 1],
    shortDegrees: [4, 1],
    fullRoman:    ["I", "V", "IV", "I"],
    shortRoman:   ["IV", "I"],
    finalFirstInversion: false,
  },
  half: {
    fullDegrees:  [1, 6, 4, 5],
    shortDegrees: [4, 5],
    fullRoman:    ["I", "vi", "IV", "V"],
    shortRoman:   ["IV", "V"],
    finalFirstInversion: false,
  },
  deceptive: {
    fullDegrees:  [1, 4, 5, 6],
    shortDegrees: [5, 6],
    fullRoman:    ["I", "IV", "V", "vi"],
    shortRoman:   ["V", "vi"],
    finalFirstInversion: false,
  },
};

// ─── Tempo settings ───────────────────────────────────────────────────────────

const TEMPO_SETTINGS: Record<CadenceTempo, { chordDur: number; gap: number }> = {
  slow:   { chordDur: 1.4, gap: 0.2 },
  medium: { chordDur: 0.95, gap: 0.05 },
  fast:   { chordDur: 0.6,  gap: 0.05 },
};

// ─── FSRS item ID ─────────────────────────────────────────────────────────────

/**
 * Both context lengths map to the same curriculum item. Context length is a
 * difficulty knob on the same skill (recognising the cadence type), not a
 * separate skill — so short-context drilling credits the same card the
 * spaced-repetition queue uses, rather than an orphaned `exercise-cadence::`
 * card invisible to node/track mastery.
 */
export function cadenceDrillItemId(
  type: CadenceType,
  _contextLength: CadenceContextLength,
): string {
  return `cadence-recognition::${type}`;
}

// ─── Stats key ────────────────────────────────────────────────────────────────

/**
 * Stable key for DrillStatsRecord.
 * Format: `cadence-drill|${type}|${contextLength}|${tonic}`
 * where tonic is MIDI number for fixed or "rng" for random.
 */
export function cadenceDrillStatsKey(
  type: CadenceType,
  contextLength: CadenceContextLength,
  config: Pick<CadenceExerciseConfig, "tonic" | "fixedTonicMidi">,
): string {
  const tonicPart = config.tonic === "random" ? "rng" : String(config.fixedTonicMidi);
  return `cadence-drill|${type}|${contextLength}|${tonicPart}`;
}

// ─── Fixed-key helper ─────────────────────────────────────────────────────────

const SEMI_TO_LETTER: Record<number, { letter: string; accidental: number }> = {
  0:  { letter: "C", accidental: 0 },
  1:  { letter: "D", accidental: -1 },
  2:  { letter: "D", accidental: 0 },
  3:  { letter: "E", accidental: -1 },
  4:  { letter: "E", accidental: 0 },
  5:  { letter: "F", accidental: 0 },
  6:  { letter: "F", accidental: 1 },
  7:  { letter: "G", accidental: 0 },
  8:  { letter: "A", accidental: -1 },
  9:  { letter: "A", accidental: 0 },
  10: { letter: "B", accidental: -1 },
  11: { letter: "B", accidental: 0 },
};

function buildFixedKey(tonicMidi: number): KeyChoice {
  const semi = ((tonicMidi - 60) % 12 + 12) % 12;
  const { letter, accidental } = SEMI_TO_LETTER[semi]!;
  const accSym = accidental === -1 ? "♭" : accidental === 1 ? "♯" : "";
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi,
    name: `${letter}${accSym} ${MODE_DISPLAY_NAMES["ionian"]}`,
  };
}

// ─── Audio builder ────────────────────────────────────────────────────────────

function buildCadenceAudio(
  key: KeyChoice,
  degrees: number[],
  chordDur: number,
  gap: number,
  finalFirstInversion: boolean,
): AudioPlan {
  const builder = new AudioPlanBuilder();
  const qualities = DIATONIC_TRIAD_QUALITIES[key.mode];
  let prevVoicing: number[] | null = null;

  for (let i = 0; i < degrees.length; i++) {
    const deg = degrees[i]!;
    const rootMidi = key.tonicMidi + degreeOffset(deg, key.mode);
    const quality = qualities[deg - 1]!;
    const triad = buildChord(rootMidi, quality);
    const isFinal = i === degrees.length - 1;

    let voicing: number[];
    let bassNote: number;

    if (isFinal && finalFirstInversion && triad.length >= 3) {
      // First inversion: 3rd in the bass, root an octave up.
      const thirdMidi = triad[1]!;
      const fifthMidi = triad[2]!;
      voicing = [thirdMidi, fifthMidi, rootMidi + 12];
      bassNote = thirdMidi - 12;
    } else {
      voicing = smoothVoicing(prevVoicing, triad);
      bassNote = rootMidi - 12;
    }
    prevVoicing = voicing;

    builder.addNote(bassNote, chordDur, 0.11, "bass");
    builder.addChord(voicing, chordDur, 0.07, "harmony");
    builder.advance(chordDur + gap);
  }

  builder.advance(0.3);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildCadenceExerciseRound(
  config: CadenceExerciseConfig,
  seed: number,
): CadenceExerciseRound {
  const rng = new Rng(seed);

  const cadenceType = rng.pick(config.cadences as CadenceType[]);
  const spec = CADENCE_SPECS[cadenceType];
  const { chordDur, gap } = TEMPO_SETTINGS[config.tempo];

  const key: KeyChoice =
    config.tonic === "fixed"
      ? buildFixedKey(config.fixedTonicMidi)
      : pickKey(rng, "major");

  const degrees =
    config.contextLength === "short" ? spec.shortDegrees : spec.fullDegrees;
  const romanLabels =
    config.contextLength === "short" ? spec.shortRoman : spec.fullRoman;

  const audioPlan = buildCadenceAudio(
    key, degrees, chordDur, gap, spec.finalFirstInversion,
  );
  // Teach audio always uses full context at a slow tempo.
  const teachAudio = buildCadenceAudio(
    key, spec.fullDegrees, 1.6, 0.2, spec.finalFirstInversion,
  );

  return {
    cadenceType,
    keyName: key.name,
    tonicMidi: key.tonicMidi,
    contextLength: config.contextLength,
    romanLabels,
    audioPlan,
    teachAudio,
  };
}
